import { Schema } from "effect";
import {
  BrandAssetHash,
  BrandColor,
  BrandColorInputs,
  BrandDisplayName,
  BrandLogoDescriptor,
  DerivedBrandColorTokens,
  DerivedBrandFonts,
} from "./branding.model.schema";

/**
 * Resolved source swatches retained alongside safe semantic tokens.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ResolvedBrandSeeds } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function accent(seeds: ResolvedBrandSeeds) { return seeds.accent; }
 * ```
 */
export const ResolvedBrandSeeds = Schema.Struct({
  primary: BrandColor,
  accent: BrandColor,
  supporting: BrandColor,
});

/**
 * Decoded resolved source swatches.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ResolvedBrandSeeds } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function primary(seeds: ResolvedBrandSeeds) { return seeds.primary; }
 * ```
 */
export type ResolvedBrandSeeds = typeof ResolvedBrandSeeds.Type;

/**
 * Immutable terminal-facing publication content with explicit default/custom state.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PublishedBrandingContent } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * Schema.decodeUnknownSync(PublishedBrandingContent)({ kind: "default" });
 * ```
 */
export const PublishedBrandingContent = Schema.Union(
  [Schema.Struct({ kind: Schema.Literal("default") }),
  Schema.Struct({
    kind: Schema.Literal("custom"),
    displayName: Schema.optional(BrandDisplayName),
    colorInputs: BrandColorInputs,
    resolvedSeeds: ResolvedBrandSeeds,
    primaryLogo: BrandLogoDescriptor,
    compactMark: Schema.optional(BrandLogoDescriptor),
    colorTokens: DerivedBrandColorTokens,
    fonts: DerivedBrandFonts,
  })],
);

/**
 * Decoded immutable publication content.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PublishedBrandingContent } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * const content: PublishedBrandingContent = { kind: "default" };
 * ```
 */
export type PublishedBrandingContent = typeof PublishedBrandingContent.Type;

/**
 * Complete public branding manifest consumed atomically by FrontDesk+ terminals.
 *
 * It contains neither an unpublished draft nor an administrative actor. Asset locations are derived
 * from content hashes by the trusted runtime rather than supplied as external URLs.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PublishedBrandingManifest } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function version(manifest: PublishedBrandingManifest) { return manifest.publicationVersion; }
 * ```
 */
export const PublishedBrandingManifest = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  catalogVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(1))),
  publicationVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  fingerprint: BrandAssetHash,
  publishedAt: Schema.DateTimeUtcFromString,
  content: PublishedBrandingContent,
});

/**
 * Decoded terminal-facing branding manifest.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PublishedBrandingManifest } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function isCustom(manifest: PublishedBrandingManifest) { return manifest.content.kind === "custom"; }
 * ```
 */
export type PublishedBrandingManifest = typeof PublishedBrandingManifest.Type;

/**
 * Bounded long-wait result for publication-version changes.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandingVersionWaitResult } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * Schema.decodeUnknownSync(BrandingVersionWaitResult)({ outcome: "unchanged", publicationVersion: 2 });
 * ```
 */
export const BrandingVersionWaitResult = Schema.Union(
  [Schema.Struct({
    outcome: Schema.Literal("changed"),
    manifest: PublishedBrandingManifest,
  }),
  Schema.Struct({
    outcome: Schema.Literal("unchanged"),
    publicationVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  }),
  Schema.Struct({ outcome: Schema.Literal("unavailable") })],
);

/**
 * Decoded publication-version wait result.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingVersionWaitResult } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * const result: BrandingVersionWaitResult = { outcome: "unavailable" };
 * ```
 */
export type BrandingVersionWaitResult = typeof BrandingVersionWaitResult.Type;

/**
 * Immutable branding asset response metadata.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingAssetResponse } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function etag(response: BrandingAssetResponse) { return response.assetHash; }
 * ```
 */
export const BrandingAssetResponse = Schema.Struct({
  assetHash: BrandAssetHash,
  mediaType: Schema.Literals(
    ["image/png",
    "image/webp",
    "image/svg+xml",
    "font/woff2",
    "font/ttf",
    "font/otf"],
  ),
  byteLength: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
});

/**
 * Decoded immutable branding asset response metadata.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingAssetResponse } from "@ascleo/frontdesk-contracts/branding/public";
 *
 * function size(response: BrandingAssetResponse) { return response.byteLength; }
 * ```
 */
export type BrandingAssetResponse = typeof BrandingAssetResponse.Type;
